// WFHLIST (2026-10-16): the country label, the accent-insensitive matcher and the
// "Name - WFH from Country" label. ONE module, so every surface says it the same way.
//
// WHY A MODULE AND NOT A HELPER INSIDE A COMPONENT. Point 3 names FOUR surfaces that must show the
// same label: the absence register, the notification bell, the calendar entries and the emails to
// the manager and the employee. Three of those are frontend and one is the server, so the frontend
// needs one definition that cannot drift between its own three screens. The server has its own,
// deliberately, because an email is rendered where no browser is: `wfh_country.py` holds the list
// and `email_i18n.py` the wording, and a verify script asserts the two agree about the hyphen and
// the shape.
//
// IT HAS NO IMPORTS AND NO COMPONENT, which is the lesson POLICYHR2 paid for: a value that gates
// or labels something must not live in a module that tests routinely replace with a mock.

// FOLD ACCENTS AND CASE, so "cote" finds "Côte d'Ivoire" and "senegal" finds "Sénégal".
//
// NFD then strip the combining marks: that is one rule for every accent rather than a table of
// substitutions somebody has to extend for the next country. The apostrophe is left alone - it is
// part of the name, not an accent, and a search for "ivoire" should still match.
export function fold(text) {
  return String(text == null ? '' : text)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

// MATCH ANYWHERE IN THE NAME, not just the start: point 2 asks for "a few letters" to filter, and
// somebody typing "ivoire" or "uni" is as likely as somebody typing the first letters.
export function matches(country, query) {
  const q = fold(query);
  if (!q) return true;
  return fold(country).includes(q);
}

// The displayed label for a stored value. Falls back to the STORED VALUE when no translation
// exists, which is what keeps a legacy or unrecognised country visible rather than blank: a
// historical declaration must still render what it actually holds.
export function countryLabel(t, stored) {
  if (!stored) return '';
  const key = `wfhCountries.${stored}`;
  const label = t(key);
  // i18next returns the key itself when there is no entry; treat that as "no translation".
  return (!label || label === key) ? stored : label;
}

// The list, SORTED ALPHABETICALLY IN THE DISPLAYED LANGUAGE.
//
// THE SORT IS DONE HERE, not on the server, and that is the whole reason the server sends an
// unsorted set. The order differs by language: "United Kingdom" sorts last-ish in English while
// "Royaume-Uni" sits under R in French, and "South Africa" / "Afrique du Sud" move from S to A.
// A single server-side sort could only ever be correct for one language.
//
// localeCompare WITH THE ACTIVE LANGUAGE, so accented names sort where a reader of that language
// expects ("Sénégal" beside "Senegal", not after "Zimbabwe").
export function sortedCountries(t, codes, lang) {
  return [...(codes || [])]
    .map(stored => ({ stored, label: countryLabel(t, stored) }))
    .sort((a, b) => a.label.localeCompare(b.label, lang || undefined, { sensitivity: 'base' }));
}

// POINT 3: the ONE label. "<Employee name> - WFH from <Country>".
//
// A SIMPLE HYPHEN, never an em dash, which check_dashes.py enforces repository-wide.
//
// A PLAIN WFH WITH NO COUNTRY KEEPS ITS CURRENT LABEL, which is why this returns null in that
// case rather than inventing wording: the caller then renders exactly what it rendered before, and
// this function cannot change the appearance of an ordinary from-home WFH day.
export function wfhFromCountryLabel(t, name, stored) {
  if (!stored) return null;
  return t('leaveForm.wfhFromCountry', {
    name: name || '',
    country: countryLabel(t, stored),
  });
}
